"use client";

import Image from "next/image";
import Link from "next/link";
import { useState } from "react";
import {
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import {
  BlocksRenderer,
  type BlocksContent,
} from "@strapi/blocks-react-renderer";

import type {
  BlockProps,
  StrapiBlock,
} from "@/app/components/blocks/types";
import { Heading } from "@/app/design-system/atoms/Heading";

type Media = {
  id?: number;
  documentId?: string;
  url?: string | null;
  alternativeText?: string | null;
};

type Offre = {
  id?: number;
  documentId?: string;
  profils?: string | null;
  titre?: string | null;
  descriptionOffre?: BlocksContent | null;
};

type AucuneOffre = {
  id?: number;
  titre?: string | null;
  sousTitre?: string | null;
  description?: string | null;
  image?: Media | null;
};

type OffreEmploiBlock =
  StrapiBlock & {
    __component:
      "blocks.offre-emploi";

    titre?: string | null;

    affichage?:
      | "offres"
      | "aucune_offre"
      | null;

    offres?: Offre[];

    aucunOffre?:
      | AucuneOffre
      | null;

    /*
     * Cette valeur ne vient pas directement
     * du bloc Strapi.
     *
     * Elle est ajoutée dans :
     * [slug]/[page]/page.tsx
     */
    agencyEmail?: string | null;
  };

/* =========================================================
   TITRE
   ========================================================= */

function HighlightTitle({
  title,
}: {
  title: string;
}) {
  const words =
    title.split(" ");

  if (words.length < 2) {
    return <>{title}</>;
  }

  return (
    <>
      {words[0]}{" "}

      <span className="text-secondary">
        {words
          .slice(1)
          .join(" ")}
      </span>
    </>
  );
}

/* =========================================================
   CANDIDATURE SPONTANÉE
   ========================================================= */

function CandidatureSpontanee({
  email,
}: {
  email?: string | null;
}) {
  const agencyEmail =
    email?.trim() || "";

  /*
   * Si l'agence n'a pas d'email,
   * on n'affiche pas cette section.
   */
  if (!agencyEmail) {
    return null;
  }

  return (
    <section
      className="overflow-hidden bg-muted"
      id="candidature-spontanee"
    >
      <div className="lms-container">
        <div className="mx-auto grid min-h-[500px] max-w-[1250px] items-center gap-4 lg:grid-cols-[52%_48%]">
          {/* IMAGE */}
          <div className="hidden lg:relative lg:flex min-h-[500px] items-end justify-center">
            <div className="absolute bottom-[110px] right-[35px] h-[280px] w-[280px] rounded-full bg-greenPastel lg:h-[320px] lg:w-[320px]" />

            <div className="relative z-10 h-[520px] w-full max-w-[620px] ">
              <Image
                src="https://site-lms.fra1.digitaloceanspaces.com/69b13c8e04e894bb56bdbc2b412fac7c2ca11668_1_47c20412ea.png"
                alt="Candidature spontanée"
                fill
                priority
                className="origin-bottom scale-[1.18] object-contain object-bottom"
                sizes="(max-width: 1024px) 100vw, 620px"
              />
            </div>
          </div>

          {/* CONTENU */}
          <div className="max-w-[520px] py-12 lg:py-16">
            <h2 className="font-playfair text-4xl font-bold leading-tight text-primaryGreen lg:text-5xl">
              Candidature
              <br />
              spontanée
            </h2>

            <p className="mt-4 text-xl leading-snug text-textColor">
              Vous ne trouvez pas
              d&apos;offre correspondant
              à votre profil ?
            </p>

            <p className="mt-4 text-xl font-bold text-textColor">
              Envoyez votre CV à
              l&apos;adresse suivante :
            </p>

            <a
              href={`mailto:${agencyEmail}`}
              className="mt-2 inline-block break-all text-xl text-primaryGreen hover:underline lg:text-2xl"
            >
              {agencyEmail}
            </a>

            <p className="mt-7 text-xl leading-relaxed text-textColor">
              Nous étudierons votre profil
              avec attention.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* =========================================================
   AUCUNE OFFRE
   ========================================================= */

function EmptyOffer({
  data,
  agencyEmail,
}: {
  data?: AucuneOffre | null;
  agencyEmail?: string | null;
}) {
  if (!data) {
    return null;
  }

  const imageUrl =
    data.image?.url || "";

  const imageAlt =
    data.image?.alternativeText ||
    data.titre ||
    "Aucune offre disponible";

  return (
    <>
      <section className="bg-white py-8 lg:py-10">
        <div className="lms-container text-center">

          {/* IMAGE */}
          {imageUrl && (
            <div className="relative mx-auto mb-5 h-[260px] w-[260px] lg:h-[300px] lg:w-[300px]">
              <Image
                src={imageUrl}
                alt={imageAlt}
                fill
                className="object-contain"
                sizes="300px"
              />
            </div>
          )}

          {/* TITRE */}
          {data.titre && (
            <h2 className="font-playfair text-3xl font-bold text-primaryGreen lg:text-4xl">
              {data.titre}
            </h2>
          )}

          {/* SOUS-TITRE */}
          {data.sousTitre && (
            <p className="mx-auto mt-4 max-w-[650px] text-xl leading-snug text-textColor">
              {data.sousTitre}
            </p>
          )}

          <div className="mx-auto my-4 h-[4px] w-24 rounded-full bg-primaryGreen" />

         
            <p className="mx-auto max-w-[1050px] whitespace-pre-line text-lg leading-relaxed text-textColor">
              Malheureusement, nous ne proposons pas d’offres d’emploi actuellement. N’hésitez toutefois pas à consulter régulièrement cette page : de nouvelles opportunités pourront être publiées prochainement. Vous pouvez également nous suivre sur nos différents canaux de communication afin d’être informé(e) de nos futurs recrutements
            </p>
          
        </div>
      </section>

      <CandidatureSpontanee
        email={agencyEmail}
      />
    </>
  );
}

/* =========================================================
   COMPOSANT PRINCIPAL
   ========================================================= */

export default function OffreEmploi({
  data,
}: BlockProps<OffreEmploiBlock>) {
  const offres =
    Array.isArray(data.offres)
      ? data.offres
      : [];

  const agencyEmail =
    data.agencyEmail?.trim() || "";

  const [
    activeIndex,
    setActiveIndex,
  ] = useState(0);

  const [
    isOpen,
    setIsOpen,
  ] = useState(true);

  /* =======================================================
     AUCUNE OFFRE
     ======================================================= */

  if (
    data.affichage ===
    "aucune_offre"
  ) {
    return (
      <EmptyOffer
        data={data.aucunOffre}
        agencyEmail={agencyEmail}
      />
    );
  }

  /* =======================================================
     OFFRES SÉLECTIONNÉES MAIS LISTE VIDE
     ======================================================= */

  if (
    data.affichage === "offres" &&
    offres.length === 0
  ) {
    return (
      <CandidatureSpontanee
        email={agencyEmail}
      />
    );
  }

  const activeOffre =
    offres[activeIndex] ??
    offres[0];

  /*
   * Même s'il n'y a finalement
   * aucune offre exploitable,
   * on garde la candidature spontanée.
   */
  if (!activeOffre) {
    return (
      <CandidatureSpontanee
        email={agencyEmail}
      />
    );
  }

  const changeOffre = (
    index: number,
  ) => {
    setActiveIndex(index);
    setIsOpen(true);
  };

  return (
    <>
      <section className="bg-white py-8 lg:py-20">
        <div className="lms-container">

          {/* TITRE */}
          {data.titre && (
            <Heading
              as="h2"
              className="mb-10 text-center font-playfair text-4xl font-bold text-[#343434] lg:text-5xl"
            >
              <HighlightTitle
                title={data.titre}
              />
            </Heading>
          )}

          {/* ONGLETS PROFILS */}
          <div className="mb-8 flex justify-center">
            <div className="flex flex-wrap justify-center gap-2 rounded-xl bg-[#F6F6F6] p-3">

              {offres.map(
                (
                  offre,
                  index,
                ) => (
                  <button
                    key={
                      offre.documentId ??
                      offre.id ??
                      index
                    }
                    type="button"
                    onClick={() =>
                      changeOffre(index)
                    }
                    className={`rounded-lg px-7 py-4 text-lg font-semibold transition ${
                      index ===
                      activeIndex
                        ? "bg-primaryGreen text-white"
                        : "text-textColor hover:bg-greenPastel"
                    }`}
                  >
                    {offre.profils}
                  </button>
                ),
              )}
            </div>
          </div>

          {/* OFFRE ACTIVE */}
          <div className="mx-auto max-w-[1200px] rounded-2xl border border-gray-200 bg-white px-7 py-4 lg:px-20 lg:py-14">

            {/* HEADER */}
            <div className="flex items-center justify-between gap-5">

              {activeOffre.titre && (
                <h3 className="font-playfair text-2xl font-bold text-primaryGreen lg:text-3xl">
                  {
                    activeOffre.titre
                  }
                </h3>
              )}

              <button
                type="button"
                onClick={() =>
                  setIsOpen(
                    (current) =>
                      !current,
                  )
                }
                aria-label={
                  isOpen
                    ? "Réduire l'offre"
                    : "Afficher l'offre"
                }
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-greenPastel text-primaryGreen transition hover:opacity-80"
              >
                {isOpen ? (
                  <ChevronUp
                    size={28}
                    strokeWidth={2.5}
                  />
                ) : (
                  <ChevronDown
                    size={28}
                    strokeWidth={2.5}
                  />
                )}
              </button>
            </div>

            {/* CONTENU OFFRE */}
            {isOpen && (
              <>
                <div className="my-7 h-[4px] w-14 rounded-full bg-primaryGreen" />

                {activeOffre.descriptionOffre && (
                  <div className="prose prose-lg max-w-none text-textColor">
                    <BlocksRenderer
                      content={
                        activeOffre.descriptionOffre
                      }
                      blocks={{
                        heading: ({
                          children,
                          level,
                        }) => {
                          if (
                            level ===
                            2
                          ) {
                            return (
                              <h2 className="mb-4 mt-7 text-2xl font-bold text-textColor">
                                {
                                  children
                                }
                              </h2>
                            );
                          }

                          if (
                            level ===
                            3
                          ) {
                            return (
                              <h3 className="mb-3 mt-6 text-xl font-bold text-textColor">
                                {
                                  children
                                }
                              </h3>
                            );
                          }

                          return (
                            <h4 className="mb-3 mt-5 font-bold text-textColor">
                              {
                                children
                              }
                            </h4>
                          );
                        },

                        paragraph: ({
                          children,
                        }) => (
                          <p className="mb-5 text-lg leading-relaxed text-textColor">
                            {
                              children
                            }
                          </p>
                        ),

                        list: ({
                          children,
                          format,
                        }) =>
                          format ===
                          "ordered" ? (
                            <ol className="mb-6 list-decimal space-y-2 pl-7 text-lg">
                              {
                                children
                              }
                            </ol>
                          ) : (
                            <ul className="mb-6 list-disc space-y-2 pl-7 text-lg">
                              {
                                children
                              }
                            </ul>
                          ),

                        link: ({
                          children,
                          url,
                        }) => (
                          <Link
                            href={url}
                            target={
                              url.startsWith(
                                "http",
                              )
                                ? "_blank"
                                : undefined
                            }
                            rel={
                              url.startsWith(
                                "http",
                              )
                                ? "noopener noreferrer"
                                : undefined
                            }
                            className="text-primaryGreen underline underline-offset-2"
                          >
                            {
                              children
                            }
                          </Link>
                        ),
                      }}
                      modifiers={{
                        bold: ({
                          children,
                        }) => (
                          <strong className="font-bold">
                            {
                              children
                            }
                          </strong>
                        ),

                        italic: ({
                          children,
                        }) => (
                          <em>
                            {
                              children
                            }
                          </em>
                        ),

                        underline: ({
                          children,
                        }) => (
                          <span className="underline">
                            {
                              children
                            }
                          </span>
                        ),
                      }}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      {/* CANDIDATURE SPONTANÉE */}
      <CandidatureSpontanee
        email={agencyEmail}
      />
    </>
  );
}